import { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OverlayExportWrapper from '@/components/shared/OverlayExportWrapper';
import FieldHeatMap from './FieldHeatMap';
import CatapultAIInsights from './CatapultAIInsights';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    TimeScale,
    RadialLinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Line, Radar, Scatter } from 'react-chartjs-2';
import { getApiUrl } from '@/config/api';
import { DashboardLayout, Header } from '@/components/shared';
import { useTheme } from '@/contexts/ThemeContext';
import { hexToRgba } from '@/utils/chartColors';

ChartJS.register(
    CategoryScale,
    LinearScale,
    TimeScale,
    RadialLinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
);

interface Activity {
    id: string;
    name: string;
    display_name: string;
    date: string;
    start_time: string;
    duration_minutes: number;
    athlete_count: number;
    tags: string[];
    has_stats: boolean;
}

interface AthleteStat {
    athlete_id: string;
    athlete_name: string;
    position: string;
    jersey: string;
    total_distance: number | null;
    total_player_load: number | null;
    total_duration: number | null;
    max_vel: number | null;
    mean_vel: number | null;
    mean_heart_rate: number | null;
    max_heart_rate: number | null;
    hsr_efforts: number | null;
    player_load_per_minute: number | null;
    high_speed_distance: number | null;
    hr_band1_pct: number | null;
    hr_band2_pct: number | null;
    hr_band3_pct: number | null;
    hr_band4_pct: number | null;
    hr_band5_pct: number | null;
    hr_band6_pct: number | null;
    hr_band7_pct: number | null;
    hr_band8_pct: number | null;
    vel_band1_dist: number | null;
    vel_band2_dist: number | null;
    vel_band3_dist: number | null;
    vel_band4_dist: number | null;
    vel_band5_dist: number | null;
    vel_band6_dist: number | null;
    accel_low: number | null;
    accel_med: number | null;
    accel_high: number | null;
    decel_low: number | null;
    decel_med: number | null;
    decel_high: number | null;
    max_accel: number | null;
    max_decel: number | null;
    velocity_threshold: number | null;
    velocity_pct_of_max: number | null;
    player_load_threshold: number | null;
    player_load_pct_of_max: number | null;
}

interface ActivitySummary {
    athlete_count: number;
    median_distance: number;
    max_distance: number;
    median_player_load: number;
    max_player_load: number;
    avg_max_velocity: number;
    top_velocity: number;
    median_heart_rate: number;
}

interface CrossActivityAvg {
    avg_distance: number;
    avg_player_load: number;
    avg_velocity: number;
    avg_heart_rate: number;
    total_distance: number;
    total_player_load: number;
    avg_top_velocity: number;
    avg_max_heart_rate: number;
}

interface ActivityDetail {
    activity: Activity;
    athletes: AthleteStat[];
    summary: ActivitySummary;
    cross_activity_avg: CrossActivityAvg;
}

interface AthleteSession {
    date: string;
    activity_id: string;
    activity_name: string;
    total_distance: number | null;
    total_player_load: number | null;
    total_duration: number | null;
    max_vel: number | null;
    mean_heart_rate: number | null;
    max_heart_rate: number | null;
    hsr_efforts: number | null;
    hr_band1_pct: number | null;
    hr_band2_pct: number | null;
    hr_band3_pct: number | null;
    hr_band4_pct: number | null;
    hr_band5_pct: number | null;
    hr_band6_pct: number | null;
    hr_band7_pct: number | null;
    hr_band8_pct: number | null;
    team_avg_distance: number | null;
    team_avg_player_load: number | null;
    team_avg_max_vel: number | null;
    team_avg_mean_hr: number | null;
}

interface AthleteProfile {
    id: string;
    name: string;
    jersey: string;
    position: string;
    velocity_max: number | null;
    heart_rate_max: number | null;
    player_load_max: number | null;
}

interface Award {
    date: string;
    rank: number;
    metric: string;
    value: number;
}

interface AthleteHistory {
    athlete: AthleteProfile;
    sessions: AthleteSession[];
    awards: Award[];
    career_stats: {
        total_sessions: number;
        avg_distance: number;
        avg_player_load: number;
        avg_max_velocity: number;
        max_distance: number;
        max_player_load: number;
        top_velocity: number;
    };
}

const FAKE_FIRST = ['Alex','Jordan','Riley','Morgan','Casey','Taylor','Avery','Quinn','Sage','Blake','Drew','Reese','Finley','Skyler','Peyton','Logan','Harper','Emery','Rowan','Kendall','Phoenix','Hayden','Dakota','Cameron','Kai','Lennox','Ellis','Remy','Shay','Jules','Arden','Briar','Wren','Tatum','Marlow','Sutton','Lane','August','Hollis','River','Sterling','Winter','Indigo','Haven','Lark','Echo','Story'];
const FAKE_LAST = ['Smith','Chen','Park','Silva','Kim','Jones','Garcia','Patel','Brown','Lee','Wilson','Nakamura','Davis','Lopez','Moore','Clark','Hall','Young','King','Wright','Scott','Adams','Baker','Carter','Evans','Fisher','Grant','Hayes','Irwin','Jensen','Knox','Lane','Mills','Nash','Owens','Price','Quinn','Reed','Stone','Tran','Voss','Ward','York','Zane'];

function useAnonymizer() {
    const nameMapRef = useRef(new Map<string, string>());
    const nameIdxRef = useRef(0);

    const anonymizeName = useCallback((real: string): string => {
        if (!real) return real;
        const existing = nameMapRef.current.get(real);
        if (existing) return existing;
        const idx = nameIdxRef.current;
        const fake = `${FAKE_FIRST[idx % FAKE_FIRST.length]} ${FAKE_LAST[idx % FAKE_LAST.length]}`;
        nameIdxRef.current = idx + 1;
        nameMapRef.current.set(real, fake);
        return fake;
    }, []);

    const anonIf = useCallback((name: string, shouldAnon: boolean): string => {
        return shouldAnon ? anonymizeName(name) : name;
    }, [anonymizeName]);

    return { anonymizeName, anonIf };
}

function hexPoints(r: number): string {
    const cx = r, cy = r;
    return Array.from({ length: 6 }, (_, i) => {
        const angle = (Math.PI / 3) * i - Math.PI / 6;
        return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
    }).join(' ');
}

type CatTimeRange = '30d' | '60d' | '90d' | '6mo' | '1yr' | 'all' | 'custom';

const CAT_TIME_RANGE_PRESETS: { key: CatTimeRange; label: string; days: number }[] = [
    { key: '30d', label: '30 days', days: 30 },
    { key: '60d', label: '60 days', days: 60 },
    { key: '90d', label: '90 days', days: 90 },
    { key: '6mo', label: '6 months', days: 180 },
    { key: '1yr', label: '1 year', days: 365 },
    { key: 'all', label: 'All time', days: 0 },
];

const clampDateValue = (val: string): string => {
    if (!val) return val;
    const [y, m, d] = val.split('-');
    if (!y || !m || !d) return val;
    return `${y.slice(0, 4).padStart(4, '0')}-${m.slice(0, 2)}-${d.slice(0, 2)}`;
};

const POSITION_COLORS: Record<string, string> = {
    'Midfielder': '#2563EB',
    'Defender': '#059669',
    'Attacker': '#DC2626',
    'Goal Keeper': '#D97706',
};

export default function CatapultDashboard() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { isDark } = useTheme();
    const { anonIf } = useAnonymizer();

    const viewParam = searchParams.get('view') as 'activity' | 'player' | null;
    const [viewMode, setViewMode] = useState<'activity' | 'player'>(viewParam || 'activity');
    const anon = searchParams.get('anonymize') === 'true';

    const [activities, setActivities] = useState<Activity[]>([]);
    const [roster, setRoster] = useState<AthleteProfile[]>([]);
    const [selectedActivityId, setSelectedActivityId] = useState<string | null>(
        searchParams.get('activity'),
    );
    const [activityDetail, setActivityDetail] = useState<ActivityDetail | null>(null);
    const [selectedAthleteId, setSelectedAthleteId] = useState<string | null>(
        searchParams.get('athlete'),
    );
    const [athleteHistory, setAthleteHistory] = useState<AthleteHistory | null>(null);
    const [loading, setLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(false);
    const [athleteLoading, setAthleteLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [userInfo, setUserInfo] = useState<any>(null);
    const [hoveredAthleteId, setHoveredAthleteId] = useState<string | null>(null);
    const [hoverSource, setHoverSource] = useState<'meter' | 'table' | null>(null);
    const [pinnedAthleteIds, setPinnedAthleteIds] = useState<Set<string>>(new Set());
    const [selectedPositions, setSelectedPositions] = useState<Set<string>>(new Set());

    const [catTimeRange, setCatTimeRange] = useState<CatTimeRange>('60d');
    const [catCustomFrom, setCatCustomFrom] = useState('');
    const [catCustomTo, setCatCustomTo] = useState('');
    const [catAppliedFrom, setCatAppliedFrom] = useState('');
    const [catAppliedTo, setCatAppliedTo] = useState('');

    const applyCatCustomDates = () => {
        setCatAppliedFrom(catCustomFrom);
        setCatAppliedTo(catCustomTo);
    };

    const handleMeterHover = (id: string | null) => {
        if (pinnedAthleteIds.size > 0) return;
        setHoveredAthleteId(id);
        setHoverSource(id ? 'meter' : null);
    };
    const handleTableHover = (id: string | null) => {
        if (pinnedAthleteIds.size > 0) return;
        setHoveredAthleteId(id);
        setHoverSource(id ? 'table' : null);
    };
    const handleTogglePin = (id: string) => {
        setPinnedAthleteIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const highlightedIds = pinnedAthleteIds.size > 0 ? pinnedAthleteIds : (hoveredAthleteId ? new Set([hoveredAthleteId]) : null);

    useEffect(() => {
        const stored = localStorage.getItem('user_info');
        if (stored) {
            try { setUserInfo(JSON.parse(stored)); } catch {}
        }
    }, []);

    const fetchWithAuth = useCallback(
        async (url: string) => {
            const currentToken = localStorage.getItem('access');
            if (!currentToken) {
                navigate('/login');
                return null;
            }
            const resp = await fetch(getApiUrl(url), {
                headers: { Authorization: `Bearer ${currentToken}` },
            });
            if (resp.status === 401) {
                navigate('/login');
                return null;
            }
            if (!resp.ok) {
                const contentType = resp.headers.get('content-type') || '';
                if (resp.status === 404 && contentType.includes('json')) {
                    return await resp.json();
                }
                throw new Error(`API error: ${resp.status}`);
            }
            const contentType = resp.headers.get('content-type') || '';
            if (!contentType.includes('json')) {
                throw new Error('No Catapult account connected');
            }
            return resp.json();
        },
        [navigate],
    );

    useEffect(() => {
        Promise.all([
            fetchWithAuth('/api/catapult/activities/?limit=200'),
            fetchWithAuth('/api/catapult/roster/'),
        ])
            .then(([activitiesData, rosterData]) => {
                if (activitiesData?.detail) {
                    setError(activitiesData.detail);
                    return;
                }
                if (activitiesData?.activities) {
                    const acts = activitiesData.activities.filter((a: Activity) => a.has_stats);
                    setActivities(acts);
                    if (!selectedActivityId && acts.length > 0) {
                        setSelectedActivityId(acts[0].id);
                    }
                }
                if (rosterData?.athletes) {
                    setRoster(rosterData.athletes);
                    if (viewMode === 'player' && !selectedAthleteId && rosterData.athletes.length > 0) {
                        setSelectedAthleteId(rosterData.athletes[0].id);
                    }
                }
            })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (viewMode !== 'activity' || !selectedActivityId) return;
        setStatsLoading(true);
        setSearchParams((prev) => { const p = Object.fromEntries(prev); return { ...p, view: 'activity', activity: selectedActivityId }; });
        fetchWithAuth(`/api/catapult/activities/${selectedActivityId}/stats/`)
            .then((data) => {
                if (data) setActivityDetail(data);
            })
            .catch((e) => setError(e.message))
            .finally(() => setStatsLoading(false));
    }, [selectedActivityId, viewMode]);

    useEffect(() => {
        if (viewMode !== 'player' || !selectedAthleteId) return;
        if (catTimeRange === 'custom' && !catAppliedFrom && !catAppliedTo) return;
        setAthleteLoading(true);
        setSearchParams((prev) => { const p = Object.fromEntries(prev); return { ...p, view: 'player', athlete: selectedAthleteId }; });

        const limit = catTimeRange === 'all' || catTimeRange === 'custom' ? 200 : 50;
        const params = new URLSearchParams({ limit: String(limit) });
        if (catTimeRange === 'custom') {
            if (catAppliedFrom) params.set('from_date', catAppliedFrom);
            if (catAppliedTo) params.set('to_date', catAppliedTo);
        } else {
            const preset = CAT_TIME_RANGE_PRESETS.find((p) => p.key === catTimeRange);
            params.set('days', String(preset?.days ?? 60));
        }

        fetchWithAuth(`/api/catapult/athletes/${selectedAthleteId}/history/?${params.toString()}`)
            .then((data) => {
                if (!data || data.detail) return;
                setAthleteHistory(data);
            })
            .catch((e) => setError(e.message))
            .finally(() => setAthleteLoading(false));
    }, [selectedAthleteId, viewMode, catTimeRange, catAppliedFrom, catAppliedTo]);

    const handleSelectAthlete = (athleteId: string) => {
        setSelectedAthleteId(athleteId);
        setViewMode('player');
    };

    const handleSwitchView = (mode: 'activity' | 'player') => {
        setViewMode(mode);
        if (mode === 'player' && !selectedAthleteId && roster.length > 0) {
            setSelectedAthleteId(roster[0].id);
        }
    };

    const shell = (children: React.ReactNode) => (
        <div className="min-h-screen cgm-bg-primary">
            <Header userInfo={userInfo} />
            <DashboardLayout>{children}</DashboardLayout>
        </div>
    );

    if (loading) {
        return shell(
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 cgm-spinner" />
            </div>
        );
    }

    if (error) {
        const hasNoData = activities.length === 0 && roster.length === 0;
        const isCatapultError = hasNoData
            || error.includes('Catapult') || error.includes('catapult')
            || error.includes('404');
        return shell(
            <div className="max-w-lg mx-auto mt-12">
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-10 text-center space-y-4">
                    <div className="text-4xl mb-2">🏃</div>
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                        {isCatapultError ? 'Catapult Not Connected' : 'Something went wrong'}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                        {isCatapultError
                            ? 'Connect your Catapult OpenField account to access GPS tracking, workload metrics, and player performance data.'
                            : error}
                    </p>
                    <button
                        onClick={() => isCatapultError ? navigate('/connect-catapult-vector') : window.location.reload()}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors mt-2"
                    >
                        {isCatapultError ? 'Connect Catapult' : 'Retry'}
                    </button>
                </div>
            </div>
        );
    }

    const viewToggle = (
        <div className="flex items-center gap-0">
            {(['activity', 'player'] as const).map((mode) => (
                <button
                    key={mode}
                    onClick={() => handleSwitchView(mode)}
                    className={`px-3 py-1.5 text-sm font-medium transition-all cursor-pointer ${
                        viewMode === mode
                            ? 'cgm-tab-active'
                            : 'cgm-tab'
                    }`}
                >
                    {mode === 'activity' ? 'Activity' : 'Player'}
                </button>
            ))}
        </div>
    );

    // Player view
    if (viewMode === 'player') {
        return shell(
            <div className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                        <h1 className="text-2xl font-bold cgm-text-primary">
                            Catapult Dashboard
                        </h1>
                        {viewToggle}
                    </div>

                    <select
                        value={selectedAthleteId || ''}
                        onChange={(e) => setSelectedAthleteId(e.target.value)}
                        className="cgm-input px-4 py-2 text-sm min-w-[250px]"
                    >
                        {roster.map((a) => (
                            <option key={a.id} value={a.id}>
                                {anonIf(a.name, anon)} #{a.jersey} — {a.position}
                            </option>
                        ))}
                    </select>
                </div>

                {selectedAthleteId && athleteHistory && (
                    <div>
                        <h2 className="text-xl font-bold cgm-text-primary">
                            {anonIf(athleteHistory.athlete.name, anon)}
                            {athleteHistory.athlete.jersey && (
                                <span className="text-base font-normal cgm-text-muted ml-2">#{athleteHistory.athlete.jersey}</span>
                            )}
                        </h2>
                        <p className="text-sm cgm-text-secondary">{athleteHistory.athlete.position}</p>
                    </div>
                )}

                {selectedAthleteId && athleteHistory && (
                    <PlayerActivityDetail
                        athleteId={selectedAthleteId}
                        athleteName={athleteHistory.athlete.name}
                        sessions={athleteHistory.sessions}
                        fetchWithAuth={fetchWithAuth}
                    />
                )}

                {athleteLoading && (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 cgm-spinner" />
                    </div>
                )}

                {athleteHistory && !athleteLoading && (
                    <>
                        <h2 className="text-lg font-semibold cgm-text-primary border-b pb-2 pt-2">
                            Player History
                        </h2>

                        {/* Time range controls */}
                        <div className="cgm-card p-3 space-y-3">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <label className="text-xs cgm-text-secondary font-medium shrink-0">History</label>
                                <div className="flex items-center gap-1 flex-wrap">
                                    {CAT_TIME_RANGE_PRESETS.map((p) => (
                                        <button
                                            key={p.key}
                                            onClick={() => setCatTimeRange(p.key)}
                                            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer ${
                                                catTimeRange === p.key
                                                    ? 'bg-blue-600 text-white shadow-sm'
                                                    : 'cgm-text-secondary hover:cgm-text-primary hover:bg-gray-100 dark:hover:bg-gray-700'
                                            }`}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                    <button
                                        onClick={() => setCatTimeRange('custom')}
                                        className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer ${
                                            catTimeRange === 'custom'
                                                ? 'bg-blue-600 text-white shadow-sm'
                                                : 'cgm-text-secondary hover:cgm-text-primary hover:bg-gray-100 dark:hover:bg-gray-700'
                                        }`}
                                    >
                                        Custom
                                    </button>
                                </div>
                            </div>
                            {catTimeRange === 'custom' && (
                                <div className="flex items-end gap-3 flex-wrap">
                                    <div className="min-w-[150px]">
                                        <label className="block text-xs cgm-text-secondary mb-1">From</label>
                                        <input
                                            type="date"
                                            value={catCustomFrom}
                                            min="2000-01-01"
                                            max={catCustomTo || new Date().toISOString().split('T')[0]}
                                            onChange={(e) => setCatCustomFrom(clampDateValue(e.target.value))}
                                            onKeyDown={(e) => { if (e.key === 'Enter') applyCatCustomDates(); }}
                                            className="cgm-input w-full text-sm"
                                        />
                                    </div>
                                    <div className="min-w-[150px]">
                                        <label className="block text-xs cgm-text-secondary mb-1">To</label>
                                        <input
                                            type="date"
                                            value={catCustomTo}
                                            min={catCustomFrom || '2000-01-01'}
                                            max={new Date().toISOString().split('T')[0]}
                                            onChange={(e) => setCatCustomTo(clampDateValue(e.target.value))}
                                            onKeyDown={(e) => { if (e.key === 'Enter') applyCatCustomDates(); }}
                                            className="cgm-input w-full text-sm"
                                        />
                                    </div>
                                    <button
                                        onClick={applyCatCustomDates}
                                        disabled={!catCustomFrom && !catCustomTo}
                                        className="px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        Apply
                                    </button>
                                    {(catCustomFrom || catCustomTo) && (
                                        <button
                                            onClick={() => { setCatCustomFrom(''); setCatCustomTo(''); setCatAppliedFrom(''); setCatAppliedTo(''); }}
                                            className="text-xs cgm-text-secondary hover:cgm-text-primary underline pb-2 cursor-pointer"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <AthleteHistoryView history={athleteHistory} anon={anon} anonIf={anonIf} />
                    </>
                )}
            </div>
        );
    }

    // Activity view
    return shell(
        <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                    <h1 className="text-2xl font-bold cgm-text-primary">
                        Catapult Dashboard
                    </h1>
                    {viewToggle}
                </div>

                <select
                    value={selectedActivityId || ''}
                    onChange={(e) => setSelectedActivityId(e.target.value)}
                    className="cgm-input px-4 py-2 text-sm min-w-[300px]"
                >
                    {activities.map((act) => (
                        <option key={act.id} value={act.id}>
                            {act.display_name} ({act.athlete_count} athletes)
                        </option>
                    ))}
                </select>
            </div>

            {statsLoading && (
                <div className="flex items-center gap-2 cgm-text-secondary">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 cgm-spinner" />
                    Loading stats...
                </div>
            )}

            {activityDetail && !statsLoading && (
                (() => {
                    const filteredAthletes = selectedPositions.size > 0
                        ? activityDetail.athletes.filter((a) => selectedPositions.has(a.position))
                        : activityDetail.athletes;

                    const MEDAL_KEYS: { key: keyof AthleteStat; filter?: (v: number) => boolean }[] = [
                        { key: 'total_player_load' },
                        { key: 'total_distance' },
                        { key: 'max_vel', filter: (v) => v < 45 },
                        { key: 'max_accel', filter: (v) => v > 0 },
                    ];
                    const medals: Record<string, Record<string, number>> = {};
                    for (const { key, filter } of MEDAL_KEYS) {
                        const sorted = [...activityDetail.athletes]
                            .filter((a) => {
                                const v = a[key] as number;
                                return v && v > 0 && (!filter || filter(v));
                            })
                            .sort((a, b) => (b[key] as number) - (a[key] as number));
                        sorted.slice(0, 3).forEach((a, i) => {
                            if (!medals[a.athlete_id]) medals[a.athlete_id] = {};
                            medals[a.athlete_id][key] = i + 1;
                        });
                    }

                    return <>
                        <h2 className="text-lg font-semibold cgm-text-primary border-b pb-2">
                            Aggregate Stats
                        </h2>
                        <ActivityRadar
                            summary={activityDetail.summary}
                            avg={activityDetail.cross_activity_avg}
                            activity={activityDetail.activity}
                        />
                        <h2 className="text-lg font-semibold cgm-text-primary border-b pb-2 pt-2">
                            Player Breakout
                        </h2>
                        <MetricStrips
                            athletes={filteredAthletes}
                            allAthletes={activityDetail.athletes}
                            onTogglePin={handleTogglePin}
                            highlightedIds={highlightedIds}
                            onHover={handleMeterHover}
                            anon={anon}
                            anonIf={anonIf}
                            selectedPositions={selectedPositions}
                            onTogglePosition={(pos) => {
                                setSelectedPositions((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(pos)) next.delete(pos);
                                    else next.add(pos);
                                    return next;
                                });
                            }}
                        />
                        <AthleteTable
                            athletes={filteredAthletes}
                            highlightedIds={highlightedIds}
                            pinnedIds={pinnedAthleteIds}
                            medals={medals}
                            anon={anon}
                            anonIf={anonIf}
                            onSelectAthlete={handleSelectAthlete}
                            onTogglePin={handleTogglePin}
                            onHover={handleTableHover}
                        />
                    </>;
                })()
            )}
        </div>
    );
}


function ActivityRadar({
    summary,
    avg,
    activity,
}: {
    summary: ActivitySummary;
    avg: CrossActivityAvg;
    activity: Activity;
}) {
    return (
        <div className="space-y-2">
            <div className="flex justify-end px-1">
                <span className="text-xs cgm-text-muted">
                    {summary.athlete_count} athletes · {Math.round(activity.duration_minutes)} min
                </span>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <OverlayExportWrapper metricName="catapult_per_athlete_avg" showStyleToggle={false}>
                <SingleRadar
                    title="Per-Athlete Averages"
                    metrics={[
                        { label: 'Median Distance', value: summary.median_distance, avg: avg.avg_distance, unit: 'm' },
                        { label: 'Median Player Load', value: summary.median_player_load, avg: avg.avg_player_load, unit: '' },
                        { label: 'Median Heart Rate', value: summary.median_heart_rate, avg: avg.avg_heart_rate, unit: 'bpm' },
                    ]}
                    color="#F97316"
                />
                </OverlayExportWrapper>
                <OverlayExportWrapper metricName="catapult_team_totals" showStyleToggle={false}>
                <SingleRadar
                    title="Team Totals"
                    metrics={[
                        { label: 'Total Distance', value: summary.total_distance, avg: avg.total_distance, unit: 'm' },
                        { label: 'Total Player Load', value: summary.total_player_load, avg: avg.total_player_load, unit: '' },
                        { label: 'Median Top Speed', value: summary.avg_top_velocity, avg: avg.avg_top_velocity, unit: 'mph' },
                        { label: 'Median Max HR', value: summary.avg_max_heart_rate, avg: avg.avg_max_heart_rate, unit: 'bpm' },
                    ]}
                    color="#2563EB"
                />
                </OverlayExportWrapper>
            </div>
        </div>
    );
}

interface RadarMetric {
    label: string;
    value: number;
    avg: number;
    unit: string;
}

function SingleRadar({ title, metrics, color, playerLabel }: { title: string; metrics: RadarMetric[]; color: string; playerLabel?: string }) {
    const formatDelta = (val: number, avg: number) => {
        if (!avg || avg === 0) return '';
        const pct = ((val - avg) / avg) * 100;
        const sign = pct >= 0 ? '+' : '';
        return `${sign}${pct.toFixed(0)}%`;
    };

    const formatVal = (v: number, unit?: string) => {
        if (unit === 'm' && v >= 1000) return `${(v / 1000).toFixed(1)} km`;
        if (v >= 10000) return `${(v / 1000).toFixed(1)}k`;
        return Number.isInteger(v) ? v.toString() : v.toFixed(1);
    };

    const normalized = metrics.map((m) => {
        const ceiling = Math.max(m.value, m.avg) * 1.3 || 1;
        return {
            thisActivity: (m.value / ceiling) * 100,
            average: (m.avg / ceiling) * 100,
        };
    });

    const labels = metrics.map((m) => {
        const val = m.value > 0 ? formatVal(m.value, m.unit) : '—';
        const delta = m.value > 0 && m.avg > 0 ? ` (${formatDelta(m.value, m.avg)})` : '';
        const unitStr = (m.unit && m.unit !== 'm') ? ` ${m.unit}` : '';
        return [m.label, `${val}${unitStr}${delta}`];
    });

    const data = {
        labels,
        datasets: [
            {
                label: playerLabel || 'This Activity',
                data: normalized.map((n) => n.thisActivity),
                backgroundColor: hexToRgba(color, 0.2),
                borderColor: color,
                borderWidth: 2,
                pointBackgroundColor: color,
                pointRadius: 4,
            },
            {
                label: 'Team Avg',
                data: normalized.map((n) => n.average),
                backgroundColor: hexToRgba('#6B7280', 0.1),
                borderColor: '#9CA3AF',
                borderWidth: 1.5,
                borderDash: [4, 3],
                pointBackgroundColor: '#9CA3AF',
                pointRadius: 3,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top' as const, labels: { boxWidth: 12, font: { size: 11 } } },
            tooltip: {
                callbacks: {
                    label: (ctx: any) => {
                        const m = metrics[ctx.dataIndex];
                        const val = ctx.datasetIndex === 0 ? m.value : m.avg;
                        const formatted = val > 0 ? formatVal(val, m.unit) : '—';
                        const unitStr = (m.unit && m.unit !== 'm') ? ' ' + m.unit : '';
                        return `${ctx.dataset.label}: ${formatted}${unitStr}`;
                    },
                },
            },
        },
        scales: {
            r: {
                beginAtZero: true,
                max: 100,
                ticks: { display: false },
                pointLabels: { font: { size: 10 } },
                grid: { color: 'rgba(156, 163, 175, 0.2)' },
                angleLines: { color: 'rgba(156, 163, 175, 0.2)' },
            },
        },
    };

    return (
        <div className="cgm-card p-4">
            {title && (
                <h3 className="text-sm font-semibold cgm-text-primary mb-2">
                    {title}
                </h3>
            )}
            <div className="h-72">
                <Radar data={data} options={options} />
            </div>
        </div>
    );
}


function MetricTooltip({ text }: { text: string }) {
    const [show, setShow] = useState(false);
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const scheduleHide = () => {
        hideTimer.current = setTimeout(() => setShow(false), 200);
    };
    const cancelHide = () => {
        if (hideTimer.current) clearTimeout(hideTimer.current);
    };
    const handleEnter = (e: React.MouseEvent) => {
        cancelHide();
        const rect = (e.target as HTMLElement).getBoundingClientRect();
        setPos({ x: rect.left, y: rect.top - 4 });
        setShow(true);
    };

    const urlPattern = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlPattern);

    return (
        <>
            <span
                className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full cursor-help transition-colors duration-200"
                style={{ backgroundColor: 'var(--accent-primary)' }}
                onMouseEnter={handleEnter}
                onMouseLeave={scheduleHide}
            >
                <svg className="w-2.5 h-2.5 cgm-text-secondary" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
            </span>
            {show && (
                <div
                    className="fixed z-[9999] w-72 p-2 text-xs cgm-tooltip border"
                    style={{ left: pos.x, top: pos.y, transform: 'translateY(-100%)' }}
                    onMouseEnter={cancelHide}
                    onMouseLeave={scheduleHide}
                >
                    {parts.map((part, i) =>
                        /^https?:\/\//.test(part) ? (
                            <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline break-all">
                                {part.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                            </a>
                        ) : (
                            <span key={i}>{part}</span>
                        )
                    )}
                </div>
            )}
        </>
    );
}

function PlayerLoadTooltip() {
    const [show, setShow] = useState(false);
    return (
        <span className="relative inline-flex items-center ml-1 gap-1">
            <button
                onMouseEnter={() => setShow(true)}
                onMouseLeave={() => setShow(false)}
                onClick={() => setShow(!show)}
                className="inline-flex items-center gap-1 text-xs cgm-text-muted cursor-help"
            >
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full" style={{ backgroundColor: 'var(--accent-primary)' }}>
                    <svg className="w-2.5 h-2.5 cgm-text-secondary" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                </span>
                What is Player Load?
            </button>
            {show && (
                <div className="absolute z-50 bottom-full left-0 mb-1 w-72 p-3 text-xs cgm-tooltip border">
                    Catapult's proprietary metric measuring total body load from the
                    accelerometer — the cumulative rate of change of acceleration across
                    all three axes. Captures running, direction changes, jumps, and
                    decelerations. Higher = more physical work.
                </div>
            )}
        </span>
    );
}


function MetricStrips({
    athletes,
    allAthletes,
    onTogglePin,
    highlightedIds,
    onHover,
    anon,
    anonIf,
    selectedPositions,
    onTogglePosition,
}: {
    athletes: AthleteStat[];
    allAthletes: AthleteStat[];
    onTogglePin: (id: string) => void;
    highlightedIds: Set<string> | null;
    onHover: (id: string | null) => void;
    anon?: boolean;
    anonIf: (name: string, shouldAnon: boolean) => string;
    selectedPositions: Set<string>;
    onTogglePosition: (pos: string) => void;
}) {
    const MAX_PLAUSIBLE_VEL = 45;

    const allStrips: {
        id: string;
        label: string;
        unit: string;
        getValue: (a: AthleteStat) => number | null;
        format: (v: number) => string;
        fixedMax?: number;
        tooltip?: string;
    }[] = [
        {
            id: 'player_load',
            label: 'Player Load',
            unit: '',
            getValue: (a) => a.total_player_load,
            format: (v) => v.toFixed(0),
            tooltip: 'Player Load is the sum of the accelerations across all axes of the internal tri-axial accelerometer during movement. Read more here: https://support.catapultsports.com/hc/en-us/articles/360000510795-What-is-Player-Load',
        },
        {
            id: 'distance',
            label: 'Distance',
            unit: '',
            getValue: (a) => a.total_distance,
            format: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)} km` : `${Math.round(v)} m`,
        },
        {
            id: 'max_vel',
            label: 'Max Velocity',
            unit: 'mph',
            getValue: (a) => (a.max_vel && a.max_vel < MAX_PLAUSIBLE_VEL) ? a.max_vel : null,
            format: (v) => v.toFixed(1),
        },
        {
            id: 'max_hr',
            label: 'Max Heart Rate',
            unit: 'bpm',
            getValue: (a) => (a.max_heart_rate && a.max_heart_rate > 0) ? a.max_heart_rate : null,
            format: (v) => Math.round(v).toString(),
        },
        {
            id: 'avg_hr',
            label: 'Avg Heart Rate',
            unit: 'bpm',
            getValue: (a) => (a.mean_heart_rate && a.mean_heart_rate > 0) ? a.mean_heart_rate : null,
            format: (v) => Math.round(v).toString(),
        },
        { id: 'mean_vel', label: 'Mean Velocity', unit: 'mph', getValue: (a) => (a.mean_vel && a.mean_vel > 0) ? a.mean_vel : null, format: (v) => v.toFixed(1) },
        { id: 'pl_per_min', label: 'Player Load / Min', unit: '', getValue: (a) => (a.player_load_per_minute && a.player_load_per_minute > 0) ? a.player_load_per_minute : null, format: (v) => v.toFixed(2) },
        { id: 'hsd', label: 'High Speed Dist', unit: '', getValue: (a) => (a.high_speed_distance && a.high_speed_distance > 0) ? a.high_speed_distance : null, format: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)} km` : `${Math.round(v)} m` },
        { id: 'hr_band1', label: 'HR Band 1 %', unit: '%', getValue: (a) => a.hr_band1_pct, format: (v) => v.toFixed(1), fixedMax: 100, tooltip: 'Typically 0–55% of HR max. Light activity / recovery.' },
        { id: 'hr_band2', label: 'HR Band 2 %', unit: '%', getValue: (a) => a.hr_band2_pct, format: (v) => v.toFixed(1), fixedMax: 100, tooltip: 'Typically 55–65% of HR max. Easy aerobic effort.' },
        { id: 'hr_band3', label: 'HR Band 3 %', unit: '%', getValue: (a) => a.hr_band3_pct, format: (v) => v.toFixed(1), fixedMax: 100, tooltip: 'Typically 65–75% of HR max. Moderate aerobic effort.' },
        { id: 'hr_band4', label: 'HR Band 4 %', unit: '%', getValue: (a) => a.hr_band4_pct, format: (v) => v.toFixed(1), fixedMax: 100, tooltip: 'Typically 75–82% of HR max. Tempo / threshold effort.' },
        { id: 'hr_band5', label: 'HR Band 5 %', unit: '%', getValue: (a) => a.hr_band5_pct, format: (v) => v.toFixed(1), fixedMax: 100, tooltip: 'Typically 82–89% of HR max. High-intensity effort.' },
        { id: 'hr_band6', label: 'HR Band 6 %', unit: '%', getValue: (a) => a.hr_band6_pct, format: (v) => v.toFixed(1), fixedMax: 100, tooltip: 'Typically 89–94% of HR max. Very high intensity.' },
        { id: 'hr_band7', label: 'HR Band 7 %', unit: '%', getValue: (a) => a.hr_band7_pct, format: (v) => v.toFixed(1), fixedMax: 100, tooltip: 'Typically 94–100% of HR max. Near-maximal effort.' },
        { id: 'hr_band8', label: 'HR Band 8 %', unit: '%', getValue: (a) => a.hr_band8_pct, format: (v) => v.toFixed(1), fixedMax: 100, tooltip: 'Typically 100%+ of HR max. Maximal / supramaximal effort.' },
        { id: 'vel_band1', label: 'Vel Band 1 Dist', unit: '', getValue: (a) => a.vel_band1_dist, format: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)} km` : `${Math.round(v)} m` },
        { id: 'vel_band2', label: 'Vel Band 2 Dist', unit: '', getValue: (a) => a.vel_band2_dist, format: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)} km` : `${Math.round(v)} m` },
        { id: 'vel_band3', label: 'Vel Band 3 Dist', unit: '', getValue: (a) => a.vel_band3_dist, format: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)} km` : `${Math.round(v)} m` },
        { id: 'vel_band4', label: 'Vel Band 4 Dist', unit: '', getValue: (a) => a.vel_band4_dist, format: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)} km` : `${Math.round(v)} m` },
        { id: 'vel_band5', label: 'Vel Band 5 Dist', unit: '', getValue: (a) => a.vel_band5_dist, format: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)} km` : `${Math.round(v)} m` },
        { id: 'vel_band6', label: 'Vel Band 6 Dist', unit: '', getValue: (a) => a.vel_band6_dist, format: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)} km` : `${Math.round(v)} m` },
        { id: 'accel_low', label: 'Accel Low', unit: '', getValue: (a) => a.accel_low, format: (v) => Math.round(v).toString() },
        { id: 'accel_med', label: 'Accel Medium', unit: '', getValue: (a) => a.accel_med, format: (v) => Math.round(v).toString() },
        { id: 'accel_high', label: 'Accel High', unit: '', getValue: (a) => a.accel_high, format: (v) => Math.round(v).toString() },
        { id: 'decel_low', label: 'Decel Low', unit: '', getValue: (a) => a.decel_low, format: (v) => Math.round(v).toString() },
        { id: 'decel_med', label: 'Decel Medium', unit: '', getValue: (a) => a.decel_med, format: (v) => Math.round(v).toString() },
        { id: 'decel_high', label: 'Decel High', unit: '', getValue: (a) => a.decel_high, format: (v) => Math.round(v).toString() },
        { id: 'max_accel', label: 'Max Accel', unit: 'm/s²', getValue: (a) => (a.max_accel && a.max_accel > 0) ? a.max_accel : null, format: (v) => v.toFixed(2) },
        { id: 'max_decel', label: 'Max Deceleration', unit: 'm/s²', getValue: (a) => (a.max_decel && a.max_decel > 0) ? a.max_decel : null, format: (v) => v.toFixed(2) },
    ];

    const DEFAULT_VISIBLE = new Set(['player_load', 'distance', 'max_vel', 'max_hr', 'max_accel']);
    const [visibleMetrics, setVisibleMetrics] = useState<Set<string>>(DEFAULT_VISIBLE);

    const strips = allStrips.filter((s) => visibleMetrics.has(s.id));

    const allPositions = [...new Set(allAthletes.map((a) => a.position))].filter(Boolean);
    const LABEL_WIDTH = 155;
    const METER_END = 85;
    const PADDING_RIGHT = METER_END;
    const CIRCLE_R = 12;
    const noFilter = selectedPositions.size === 0;
    const ZOOM_WINDOW = 20;
    const [zoomStrip, setZoomStrip] = useState<{ id: string; min: number; max: number } | null>(null);

    return (
        <div className="cgm-card p-4">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold cgm-text-primary">
                        Athlete Performance
                    </h3>
                    <div className="relative group">
                        <button
                            className="w-5 h-5 flex items-center justify-center rounded-full cgm-text-secondary text-xs leading-none cursor-pointer transition-colors duration-200"
                            style={{ backgroundColor: 'var(--accent-primary)' }}
                        >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                        </button>
                        <div className="absolute left-0 top-full pt-0 z-30 hidden group-hover:block">
                        <div className="cgm-dropdown p-2 w-56 max-h-64 overflow-auto">
                            {allStrips.map((s) => (
                                <label key={s.id} className="flex items-center gap-2 px-2 py-1 cgm-dropdown-item cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={visibleMetrics.has(s.id)}
                                        onChange={() => {
                                            setVisibleMetrics((prev) => {
                                                const next = new Set(prev);
                                                if (next.has(s.id)) next.delete(s.id);
                                                else next.add(s.id);
                                                return next;
                                            });
                                        }}
                                        className="rounded"
                                    />
                                    <span className="text-xs cgm-text-primary">{s.label}</span>
                                </label>
                            ))}
                        </div>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {allPositions.map((pos) => {
                        const isActive = noFilter || selectedPositions.has(pos);
                        return (
                            <button
                                key={pos}
                                onClick={() => onTogglePosition(pos)}
                                className={`flex items-center gap-1 px-2 py-1 rounded-full border text-xs cursor-pointer transition-all ${
                                    isActive
                                        ? ''
                                        : 'opacity-40'
                                }`}
                                style={{ borderColor: 'var(--card-border)' }}
                            >
                                <span
                                    className="inline-block w-3 h-3 rounded-full"
                                    style={{ backgroundColor: isActive ? (POSITION_COLORS[pos] || '#6B7280') : '#D1D5DB' }}
                                />
                                <span className={isActive ? 'cgm-text-primary' : 'cgm-text-muted'}>{pos}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="space-y-6 overflow-y-auto overflow-x-hidden pt-8" style={{ height: 370, scrollbarGutter: 'stable' }}>
                {strips.map((strip) => {
                    const points = athletes
                        .map((a) => ({ athlete: a, value: strip.getValue(a) }))
                        .filter((p): p is { athlete: AthleteStat; value: number } => p.value != null && p.value > 0);

                    const allPoints = allAthletes
                        .map((a) => strip.getValue(a))
                        .filter((v): v is number => v != null && v > 0);
                    const rawMax = strip.fixedMax || Math.max(...allPoints, 1);
                    const secondSigFig = Math.pow(10, Math.floor(Math.log10(rawMax)) - 1);
                    const maxVal = strip.fixedMax || (Math.ceil(rawMax / secondSigFig) + 1) * secondSigFig;

                    const isZoomed = zoomStrip?.id === strip.id;
                    const zMin = isZoomed ? zoomStrip!.min : 0;
                    const zMax = isZoomed ? zoomStrip!.max : 100;
                    const zRange = zMax - zMin;

                    // Axis labels adapt to zoom
                    const axisMinVal = isZoomed ? (zMin / 100) * maxVal : 0;
                    const axisMaxVal = isZoomed ? (zMax / 100) * maxVal : maxVal;

                    const handleStripZoomClick = (e: React.MouseEvent<HTMLDivElement>) => {
                        if (isZoomed) { setZoomStrip(null); return; }
                        const rect = e.currentTarget.getBoundingClientRect();
                        // Convert click to percentage within the meter area (label to end)
                        const meterLeft = rect.left;
                        const meterWidth = rect.width;
                        const clickPct = ((e.clientX - meterLeft) / meterWidth) * 100;
                        const half = ZOOM_WINDOW / 2;
                        setZoomStrip({
                            id: strip.id,
                            min: Math.max(0, clickPct - half),
                            max: Math.min(100, clickPct + half),
                        });
                    };

                    return (
                        <div key={strip.label}>
                            <div className="relative" style={{ height: 40 }}>
                                {/* Label at left */}
                                <div className="absolute left-0 top-0 h-full flex items-center z-[5]" style={{ width: LABEL_WIDTH - 12 }}>
                                    <span className="text-xs font-medium cgm-text-secondary uppercase tracking-wide truncate">
                                        {strip.label}
                                    </span>
                                    {strip.tooltip && (
                                        <MetricTooltip text={strip.tooltip} />
                                    )}
                                    {isZoomed && (
                                        <button onClick={() => setZoomStrip(null)} className="ml-1 text-[9px] cgm-text-muted hover:cgm-text-primary cursor-pointer">
                                            (reset)
                                        </button>
                                    )}
                                </div>
                                {/* Axis start label */}
                                <div
                                    className="absolute top-0 h-full flex items-center justify-end pointer-events-none"
                                    style={{ left: 0, width: LABEL_WIDTH - 10 }}
                                >
                                    <span className="text-xs cgm-text-muted tabular-nums">
                                        {isZoomed ? strip.format(axisMinVal) : '0'}
                                    </span>
                                </div>
                                {/* Axis end label */}
                                <div
                                    className="absolute top-0 h-full flex items-center"
                                    style={{ left: `calc(100% - ${METER_END}px + 8px)` }}
                                >
                                    <span className="text-xs cgm-text-muted tabular-nums">
                                        {strip.format(axisMaxVal)}{strip.unit ? ` ${strip.unit}` : ''}
                                    </span>
                                </div>
                                {/* Clickable baseline area for zoom */}
                                <div
                                    className="absolute cursor-pointer"
                                    style={{ left: LABEL_WIDTH, right: PADDING_RIGHT, top: 0, bottom: 0 }}
                                    onClick={handleStripZoomClick}
                                />
                                <svg width="100%" height="40" className="overflow-visible pointer-events-none">
                                    <line
                                        x1={LABEL_WIDTH}
                                        y1={20}
                                        x2={`calc(100% - ${PADDING_RIGHT}px)`}
                                        y2={20}
                                        stroke="currentColor"
                                        style={{ color: 'var(--card-border)' }}
                                        strokeWidth={2}
                                    />
                                    {[0, 0.25, 0.5, 0.75, 1].map((t) => (
                                        <line
                                            key={t}
                                            x1={`calc(${LABEL_WIDTH}px + ${t} * (100% - ${LABEL_WIDTH + PADDING_RIGHT}px))`}
                                            y1={14}
                                            x2={`calc(${LABEL_WIDTH}px + ${t} * (100% - ${LABEL_WIDTH + PADDING_RIGHT}px))`}
                                            y2={26}
                                            stroke="currentColor"
                                            style={{ color: 'var(--card-border)' }}
                                            strokeWidth={1}
                                        />
                                    ))}
                                </svg>
                                {points.map((p) => {
                                    const rawPct = (p.value / maxVal) * 100;
                                    // Apply zoom: remap rawPct into visible window
                                    if (rawPct < zMin || rawPct > zMax) return null;
                                    const pct = ((rawPct - zMin) / zRange) * 100;
                                    const isHighlighted = highlightedIds ? highlightedIds.has(p.athlete.athlete_id) : false;
                                    const isGreyed = highlightedIds !== null && !isHighlighted;
                                    const color = POSITION_COLORS[p.athlete.position] || '#6B7280';

                                    return (
                                        <div
                                            key={`${strip.label}-${p.athlete.athlete_id}`}
                                            className="absolute transition-all duration-300"
                                            style={{
                                                left: `calc(${LABEL_WIDTH}px + ${pct} * (100% - ${LABEL_WIDTH + PADDING_RIGHT}px) / 100)`,
                                                top: 20 - CIRCLE_R,
                                                transform: 'translateX(-50%)',
                                                zIndex: isHighlighted ? 20 : 10,
                                            }}
                                        >
                                            {isHighlighted && (
                                                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 text-xs font-medium whitespace-nowrap shadow-lg pointer-events-none" style={{ backgroundColor: 'var(--tab-selected-bg)', color: 'var(--tab-selected-text)' }}>
                                                    {strip.format(p.value)}{strip.unit ? ` ${strip.unit}` : ''}
                                                    <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent" style={{ borderTopColor: 'var(--tab-selected-bg)' }} />
                                                </div>
                                            )}
                                            <div
                                                onMouseEnter={() => onHover(p.athlete.athlete_id)}
                                                onMouseLeave={() => onHover(null)}
                                                onClick={() => onTogglePin(p.athlete.athlete_id)}
                                                className="cursor-pointer flex items-center justify-center rounded-full border-2 transition-all duration-150"
                                                style={{
                                                    width: CIRCLE_R * 2,
                                                    height: CIRCLE_R * 2,
                                                    backgroundColor: isGreyed ? '#D1D5DB' : color,
                                                    borderColor: isGreyed ? '#D1D5DB' : color,
                                                    opacity: isGreyed ? 0.3 : 1,
                                                    transform: isHighlighted ? 'scale(1.3)' : 'scale(1)',
                                                }}
                                            >
                                                <span
                                                    className="text-white font-bold leading-none"
                                                    style={{ fontSize: 9 }}
                                                >
                                                    {p.athlete.jersey || ''}
                                                </span>
                                            </div>
                                            {isHighlighted && (
                                                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-0.5 text-center pointer-events-none whitespace-nowrap">
                                                    <span className="cgm-text-secondary" style={{ fontSize: 8, lineHeight: 1 }}>
                                                        {anonIf(p.athlete.athlete_name, !!anon).split(' ').pop()}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

        </div>
    );
}


const MEDAL_EMOJIS: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };

function AthleteTable({ athletes, highlightedIds, pinnedIds, medals, anon, anonIf, onSelectAthlete, onTogglePin, onHover }: {
    athletes: AthleteStat[];
    highlightedIds?: Set<string> | null;
    pinnedIds?: Set<string>;
    medals?: Record<string, Record<string, number>>;
    anon?: boolean;
    anonIf: (name: string, shouldAnon: boolean) => string;
    onSelectAthlete?: (id: string) => void;
    onTogglePin?: (id: string) => void;
    onHover?: (id: string | null) => void;
}) {
    const [sortKey, setSortKey] = useState<keyof AthleteStat>('total_player_load');
    const [sortAsc, setSortAsc] = useState(false);

    const handleSort = (key: keyof AthleteStat) => {
        if (sortKey === key) {
            setSortAsc(!sortAsc);
        } else {
            setSortKey(key);
            setSortAsc(false);
        }
    };

    const sorted = [...athletes].sort((a, b) => {
        const va = a[sortKey] ?? 0;
        const vb = b[sortKey] ?? 0;
        if (va < vb) return sortAsc ? -1 : 1;
        if (va > vb) return sortAsc ? 1 : -1;
        return 0;
    });

    const columns: { key: keyof AthleteStat; label: string; format?: (v: any) => string; sticky?: boolean }[] = [
        { key: 'athlete_name', label: 'Athlete', sticky: true },
        { key: 'position', label: 'Pos', sticky: true },
        { key: 'total_distance', label: 'Distance', format: (v) => v ? (v >= 1000 ? `${(v/1000).toFixed(1)} km` : `${Math.round(v)} m`) : '—' },
        { key: 'total_player_load', label: 'Player Load', format: (v) => v ? v.toFixed(1) : '—' },
        { key: 'max_vel', label: 'Max Vel', format: (v) => v ? v.toFixed(1) : '—' },
        { key: 'mean_vel', label: 'Mean Vel', format: (v) => v && v > 0 ? v.toFixed(1) : '—' },
        { key: 'mean_heart_rate', label: 'Avg HR', format: (v) => v && v > 0 ? Math.round(v).toString() : '—' },
        { key: 'max_heart_rate', label: 'Max HR', format: (v) => v && v > 0 ? v.toString() : '—' },
        { key: 'player_load_per_minute', label: 'PL/Min', format: (v) => v && v > 0 ? v.toFixed(2) : '—' },
        { key: 'high_speed_distance', label: 'HS Dist', format: (v) => v && v > 0 ? (v >= 1000 ? `${(v/1000).toFixed(1)} km` : `${Math.round(v)} m`) : '—' },
        { key: 'hsr_efforts', label: 'HSR Efforts', format: (v) => v ? v.toString() : '—' },
        { key: 'hr_band1_pct', label: 'HR B1 %', format: (v) => v != null ? `${v.toFixed(1)}%` : '—' },
        { key: 'hr_band2_pct', label: 'HR B2 %', format: (v) => v != null ? `${v.toFixed(1)}%` : '—' },
        { key: 'hr_band3_pct', label: 'HR B3 %', format: (v) => v != null ? `${v.toFixed(1)}%` : '—' },
        { key: 'hr_band4_pct', label: 'HR B4 %', format: (v) => v != null ? `${v.toFixed(1)}%` : '—' },
        { key: 'hr_band5_pct', label: 'HR B5 %', format: (v) => v != null ? `${v.toFixed(1)}%` : '—' },
        { key: 'hr_band6_pct', label: 'HR B6 %', format: (v) => v != null ? `${v.toFixed(1)}%` : '—' },
        { key: 'hr_band7_pct', label: 'HR B7 %', format: (v) => v != null ? `${v.toFixed(1)}%` : '—' },
        { key: 'hr_band8_pct', label: 'HR B8 %', format: (v) => v != null ? `${v.toFixed(1)}%` : '—' },
        { key: 'accel_low', label: 'Accel Lo', format: (v) => v != null ? Math.round(v).toString() : '—' },
        { key: 'accel_med', label: 'Accel Med', format: (v) => v != null ? Math.round(v).toString() : '—' },
        { key: 'accel_high', label: 'Accel Hi', format: (v) => v != null ? Math.round(v).toString() : '—' },
        { key: 'decel_low', label: 'Decel Lo', format: (v) => v != null ? Math.round(v).toString() : '—' },
        { key: 'decel_med', label: 'Decel Med', format: (v) => v != null ? Math.round(v).toString() : '—' },
        { key: 'decel_high', label: 'Decel Hi', format: (v) => v != null ? Math.round(v).toString() : '—' },
        { key: 'max_accel', label: 'Max Accel', format: (v) => v && v > 0 ? v.toFixed(2) : '—' },
        { key: 'max_decel', label: 'Max Decel', format: (v) => v && v > 0 ? v.toFixed(2) : '—' },
    ];

    const SortArrow = ({ col }: { col: keyof AthleteStat }) =>
        sortKey === col ? (
            <span className="ml-1">{sortAsc ? '▲' : '▼'}</span>
        ) : null;

    return (
        <div className="cgm-card overflow-hidden" style={{ height: 280 }}>
            <div className="px-4 py-2" style={{ borderBottom: '1px solid var(--card-border)' }}>
                <h3 className="text-xs font-semibold cgm-text-secondary uppercase tracking-wide">
                    Athlete Stats
                </h3>
            </div>
            <div className="overflow-auto" style={{ maxHeight: 244 }}>
                <table className="w-full text-sm">
                    <thead>
                        <tr style={{ backgroundColor: 'var(--bg-card)' }}>
                            {columns.map((col, ci) => {
                                const stickyLeft = col.sticky
                                    ? ci === 0 ? 0 : 150
                                    : undefined;
                                return (
                                <th
                                    key={col.key}
                                    onClick={() => handleSort(col.key)}
                                    className={`px-3 py-2 text-left text-xs font-medium cgm-text-secondary uppercase tracking-wider cursor-pointer select-none whitespace-nowrap ${col.sticky ? 'sticky z-20' : ''}`}
                                    style={{
                                        ...(col.sticky ? { backgroundColor: 'var(--bg-card)' } : {}),
                                        ...(stickyLeft !== undefined ? { left: stickyLeft, minWidth: ci === 0 ? 150 : 80 } : {}),
                                    }}
                                >
                                    {col.label}
                                    <SortArrow col={col.key} />
                                </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody style={{ borderColor: 'var(--card-border)' }} className="divide-y">
                        {sorted.map((athlete) => {
                            const isPinned = pinnedIds?.has(athlete.athlete_id);
                            const isHighlighted = highlightedIds ? highlightedIds.has(athlete.athlete_id) : false;
                            const isDimmed = highlightedIds !== null && highlightedIds !== undefined && highlightedIds.size > 0 && !isHighlighted;
                            return (
                            <tr
                                key={athlete.athlete_id}
                                className={`cursor-pointer ${isPinned ? 'ring-1 ring-orange-400 ring-inset' : ''}`}
                                onMouseEnter={() => onHover?.(athlete.athlete_id)}
                                onMouseLeave={() => onHover?.(null)}
                                onClick={() => onTogglePin?.(athlete.athlete_id)}
                            >
                                {columns.map((col, ci) => {
                                    const rawVal = athlete[col.key];
                                    const val = col.key === 'athlete_name' && anon ? anonIf(rawVal as string, true) : rawVal;
                                    const display = col.format ? col.format(val) : (val ?? '—');
                                    const stickyLeft = col.sticky
                                        ? ci === 0 ? 0 : 150
                                        : undefined;
                                    return (
                                        <td
                                            key={col.key}
                                            className={`px-3 py-2 cgm-text-primary whitespace-nowrap ${
                                                col.sticky
                                                    ? `sticky z-20`
                                                    : ''
                                            }`}
                                            style={{
                                                ...(col.sticky ? { borderRight: '1px solid var(--card-border)', backgroundColor: isHighlighted ? 'var(--accent-ui-subtle)' : 'var(--bg-card)' } : isHighlighted ? { backgroundColor: 'var(--accent-ui-subtle)' } : {}),
                                                ...(stickyLeft !== undefined ? { left: stickyLeft, minWidth: ci === 0 ? 150 : 80 } : {}),
                                            }}
                                        >
                                            {col.key === 'athlete_name' ? (
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className="inline-block w-2 h-2 rounded-full"
                                                        style={{
                                                            backgroundColor:
                                                                POSITION_COLORS[athlete.position] ||
                                                                '#6B7280',
                                                        }}
                                                    />
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); onSelectAthlete?.(athlete.athlete_id); }}
                                                        className="font-medium text-orange-500 hover:underline text-left cursor-pointer"
                                                    >
                                                        {display}
                                                    </button>
                                                    {athlete.jersey && (
                                                        <span className="text-xs cgm-text-muted">
                                                            #{athlete.jersey}
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span>
                                                    {display}
                                                    {medals?.[athlete.athlete_id]?.[col.key] && (
                                                        <span className="ml-1">{MEDAL_EMOJIS[medals[athlete.athlete_id][col.key]]}</span>
                                                    )}
                                                </span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


function AthleteHistoryView({ history, anon, anonIf }: { history: AthleteHistory; anon?: boolean; anonIf: (name: string, shouldAnon: boolean) => string }) {
    const { athlete, sessions, career_stats, awards } = history;

    const avgOf = (key: keyof AthleteSession) => {
        const vals = sessions.map((s) => s[key] as number).filter((v) => v && v > 0);
        return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    };

    const teamAvgOf = (key: keyof AthleteSession) => {
        const vals = sessions.map((s) => s[key] as number).filter((v) => v && v > 0);
        return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <OverlayExportWrapper metricName="catapult_player_radar" showStyleToggle={false}>
                <SingleRadar
                    title=""
                    metrics={[
                        { label: 'Avg Distance', value: career_stats.avg_distance, avg: teamAvgOf('team_avg_distance'), unit: 'm' },
                        { label: 'Avg Player Load', value: career_stats.avg_player_load, avg: teamAvgOf('team_avg_player_load'), unit: '' },
                        { label: 'Avg Top Speed', value: career_stats.avg_max_velocity, avg: teamAvgOf('team_avg_max_vel'), unit: 'mph' },
                        { label: 'Avg Heart Rate', value: avgOf('mean_heart_rate'), avg: teamAvgOf('team_avg_mean_hr'), unit: 'bpm' },
                    ]}
                    color="#F97316"
                    playerLabel={anonIf(athlete.name, !!anon)}
                />
                </OverlayExportWrapper>
                <OverlayExportWrapper metricName="catapult_awards" showStyleToggle={false}>
                <AwardsTimeline awards={awards} />
                </OverlayExportWrapper>
            </div>

            <OverlayExportWrapper metricName="catapult_player_trends" showStyleToggle={false}>
            <PlayerChartSelector sessions={sessions} hrMax={athlete.heart_rate_max} />
            </OverlayExportWrapper>
        </div>
    );
}


const MEDAL_COLORS: Record<number, string> = {
    1: '#FFD700', // gold
    2: '#C0C0C0', // silver
    3: '#CD7F32', // bronze
};

const MEDAL_LABELS: Record<number, string> = { 1: '1st', 2: '2nd', 3: '3rd' };

const AWARD_METRICS = ['Player Load', 'Distance', 'Top Speed', 'Max Accel'];

function AwardsTimeline({ awards }: { awards: Award[] }) {
    const [hoveredAward, setHoveredAward] = useState<{ award: Award; x: number; y: number } | null>(null);
    const [zoomRange, setZoomRange] = useState<{ min: number; max: number } | null>(null);

    if (awards.length === 0) {
        return (
            <div className="cgm-card p-4">
                <h3 className="text-sm font-semibold cgm-text-primary mb-3">Awards</h3>
                <div className="h-48 flex items-center justify-center text-sm cgm-text-muted">No podium finishes in recent sessions</div>
            </div>
        );
    }

    // Build sorted unique date axis
    const allDates = [...new Set(awards.map((a) => a.date))].sort();
    const dateMin = allDates[0];
    const dateMax = allDates[allDates.length - 1];
    const msMin = new Date(dateMin + 'T12:00:00').getTime();
    const msMax = new Date(dateMax + 'T12:00:00').getTime();
    const msRange = msMax - msMin || 1;

    // Group awards by metric
    const byMetric: Record<string, Award[]> = {};
    for (const a of awards) {
        if (!byMetric[a.metric]) byMetric[a.metric] = [];
        byMetric[a.metric].push(a);
    }

    // Only show metrics that have awards
    const activeMetrics = AWARD_METRICS.filter((m) => byMetric[m]?.length);

    const LABEL_W = 120;
    const COUNT_W = 40;
    const CIRCLE_R = 12;
    const ZOOM_WINDOW = 20; // % of full range to show when zoomed

    const formatVal = (metric: string, value: number) => {
        if (metric === 'Distance') return value >= 1000 ? `${(value / 1000).toFixed(1)} km` : `${Math.round(value)} m`;
        if (metric === 'Top Speed') return `${value.toFixed(1)} mph`;
        if (metric === 'Max Accel') return `${value.toFixed(2)} m/s\u00B2`;
        return value.toFixed(1);
    };

    /** Convert an award date to a raw percentage (5-95 range) on the full timeline. */
    const rawPct = (date: string) => {
        if (allDates.length === 1) return 50;
        const ms = new Date(date + 'T12:00:00').getTime();
        return ((ms - msMin) / msRange) * 90 + 5;
    };

    /** Handle click on a strip baseline to zoom/reset. */
    const handleStripClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (zoomRange) {
            setZoomRange(null);
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const clickPct = ((e.clientX - rect.left) / rect.width) * 100;
        const half = ZOOM_WINDOW / 2;
        setZoomRange({
            min: Math.max(0, clickPct - half),
            max: Math.min(100, clickPct + half),
        });
    };

    /** Remap a raw pct into the visible zoom window. Returns null if out of range. */
    const zoomedPct = (pct: number): number | null => {
        if (!zoomRange) return pct;
        if (pct < zoomRange.min || pct > zoomRange.max) return null;
        return ((pct - zoomRange.min) / (zoomRange.max - zoomRange.min)) * 100;
    };

    /** Convert a percentage back to a date string for axis labels. */
    const pctToDate = (pct: number) => {
        const rawPctNorm = (pct - 5) / 90; // reverse the 5-95 mapping
        const ms = msMin + rawPctNorm * msRange;
        return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const axisStartLabel = zoomRange ? pctToDate(zoomRange.min) : new Date(dateMin + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const axisEndLabel = zoomRange ? pctToDate(zoomRange.max) : new Date(dateMax + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    return (
        <div className="cgm-card p-4 relative">
            <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold cgm-text-primary">
                    Awards
                    <span className="ml-2 text-xs font-normal cgm-text-muted">{awards.length} podiums</span>
                </h3>
                {zoomRange && (
                    <button
                        onClick={() => setZoomRange(null)}
                        className="text-[10px] cgm-text-muted hover:cgm-text-primary transition-colors cursor-pointer"
                    >
                        Reset zoom
                    </button>
                )}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mb-3 ml-auto justify-end">
                {[1, 2, 3].map((rank) => (
                    <div key={rank} className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: MEDAL_COLORS[rank] }} />
                        <span className="text-[10px] cgm-text-secondary">{MEDAL_LABELS[rank]}</span>
                    </div>
                ))}
            </div>

            {/* Strips */}
            <div className="space-y-3">
                {activeMetrics.map((metric) => {
                    const metricAwards = byMetric[metric] || [];
                    return (
                        <div key={metric} className="flex items-center gap-0">
                            {/* Label */}
                            <div className="flex-shrink-0 text-xs font-medium cgm-text-secondary uppercase tracking-wide" style={{ width: LABEL_W }}>
                                {metric}
                            </div>

                            {/* Strip — click to zoom/reset */}
                            <div
                                className="flex-1 relative cursor-pointer"
                                style={{ height: CIRCLE_R * 2 + 8 }}
                                onClick={handleStripClick}
                            >
                                {/* Baseline */}
                                <div
                                    className="absolute top-1/2 left-0 right-0 -translate-y-1/2"
                                    style={{ height: 2, backgroundColor: 'var(--accent-primary, #e5e7eb)', opacity: 0.4 }}
                                />

                                {/* Award circles */}
                                {metricAwards.map((award, i) => {
                                    const pct = rawPct(award.date);
                                    const displayPct = zoomedPct(pct);
                                    if (displayPct === null) return null;
                                    return (
                                        <div
                                            key={`${award.date}-${award.rank}-${i}`}
                                            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-125"
                                            style={{
                                                left: `${displayPct}%`,
                                                width: CIRCLE_R * 2,
                                                height: CIRCLE_R * 2,
                                                borderRadius: '50%',
                                                backgroundColor: MEDAL_COLORS[award.rank],
                                                zIndex: 4 - award.rank,
                                            }}
                                            onMouseEnter={(e) => {
                                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                setHoveredAward({ award, x: rect.left + rect.width / 2, y: rect.top });
                                            }}
                                            onMouseLeave={() => setHoveredAward(null)}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                setHoveredAward((prev) =>
                                                    prev?.award === award ? null : { award, x: rect.left + rect.width / 2, y: rect.top }
                                                );
                                            }}
                                        >
                                            <span className="text-[9px] font-bold" style={{ color: award.rank === 2 ? '#374151' : '#fff' }}>
                                                {award.rank}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Count */}
                            <div className="flex-shrink-0 text-xs font-semibold cgm-text-muted text-right" style={{ width: COUNT_W }}>
                                {metricAwards.length}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Date axis labels */}
            <div className="flex items-center mt-2" style={{ paddingLeft: LABEL_W, paddingRight: COUNT_W }}>
                <span className="text-[10px] cgm-text-muted">{axisStartLabel}</span>
                <span className="flex-1" />
                {axisStartLabel !== axisEndLabel && (
                    <span className="text-[10px] cgm-text-muted">{axisEndLabel}</span>
                )}
            </div>

            {/* Tooltip — rendered via portal to escape stacking context */}
            {hoveredAward && ReactDOM.createPortal(
                <div
                    className="fixed px-3 py-2 shadow-lg text-xs pointer-events-none"
                    style={{
                        left: hoveredAward.x,
                        top: hoveredAward.y - 8,
                        transform: 'translate(-50%, -100%)',
                        zIndex: 9999,
                        backgroundColor: 'var(--bg-card)',
                        border: '1px solid var(--card-border)',
                    }}
                >
                    <div className="font-semibold cgm-text-primary">
                        {MEDAL_LABELS[hoveredAward.award.rank]} - {hoveredAward.award.metric}
                    </div>
                    <div className="cgm-text-secondary">
                        {formatVal(hoveredAward.award.metric, hoveredAward.award.value)} &middot;{' '}
                        {new Date(hoveredAward.award.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

interface GpsPoint {
    x: number;
    y: number;
    v: number;
    hr: number;
    ts: number;
}

function PlayerActivityDetail({
    athleteId,
    athleteName,
    sessions,
    fetchWithAuth,
}: {
    athleteId: string;
    athleteName?: string;
    sessions: AthleteSession[];
    fetchWithAuth: (url: string) => Promise<any>;
}) {
    const { isDark } = useTheme();
    // Only show sessions that likely have GPS data (distance > 0)
    const gpsSessions = sessions.filter((s) => s.total_distance && s.total_distance > 0);
    const [selectedActId, setSelectedActId] = useState(gpsSessions[gpsSessions.length - 1]?.activity_id || '');
    const [gpsData, setGpsData] = useState<GpsPoint[] | null>(null);
    const [actStats, setActStats] = useState<AthleteStat | null>(null);
    const [gpsLoading, setGpsLoading] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    const [heatMode, setHeatMode] = useState<'time' | 'speed' | 'hr'>('time');

    useEffect(() => {
        if (!selectedActId || !athleteId) return;
        setGpsLoading(true);
        setGpsData(null);
        setActStats(null);

        Promise.all([
            fetchWithAuth(`/api/catapult/activities/${selectedActId}/athletes/${athleteId}/sensor/?max_points=800`),
            fetchWithAuth(`/api/catapult/activities/${selectedActId}/stats/`),
        ]).then(([sensorData, statsData]) => {
            if (sensorData?.points) setGpsData(sensorData.points);
            if (statsData?.athletes) {
                const me = statsData.athletes.find((a: AthleteStat) => a.athlete_id === athleteId);
                if (me) setActStats(me);
            }
        }).catch(() => {})
          .finally(() => setGpsLoading(false));
    }, [selectedActId, athleteId]);

    if (gpsSessions.length === 0) return null;

    // Build overlay data for canvas rendering
    const selectedSession = gpsSessions.find((s) => s.activity_id === selectedActId);
    const sessionLabel = selectedSession
        ? `${selectedSession.activity_name} ${selectedSession.date ? '\u00b7 ' + new Date(selectedSession.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}`
        : undefined;

    const overlayStats: CanvasOverlayStats | undefined = actStats ? {
        distance: actStats.total_distance ? (actStats.total_distance >= 1000 ? `${(actStats.total_distance / 1000).toFixed(1)} km` : `${Math.round(actStats.total_distance)} m`) : '--',
        playerLoad: actStats.total_player_load ? actStats.total_player_load.toFixed(1) : '--',
        topSpeed: actStats.max_vel ? `${actStats.max_vel.toFixed(1)} mph` : '--',
        maxHR: actStats.max_heart_rate && actStats.max_heart_rate > 0 ? `${actStats.max_heart_rate} bpm` : '--',
    } : undefined;

    return (
        <div className="cgm-card p-4">
            <div className="flex items-center gap-3 mb-4">
                <h3 className="text-sm font-semibold cgm-text-primary">Activity Detail</h3>
                <select
                    value={selectedActId}
                    onChange={(e) => setSelectedActId(e.target.value)}
                    className="cgm-input px-3 py-1.5 text-sm"
                >
                    {[...gpsSessions].reverse().map((s) => (
                        <option key={s.activity_id} value={s.activity_id}>
                            {s.activity_name}
                        </option>
                    ))}
                </select>
            </div>

            {gpsLoading && (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 cgm-spinner" />
                </div>
            )}

            {!gpsLoading && gpsData && gpsData.length > 0 && (
                <>
                {/* Mode tabs — outside the export wrapper so they don't appear in PNG */}
                <div className="flex items-center gap-0 mb-3">
                    {(['time', 'speed', 'hr'] as const).map((m) => (
                        <button
                            key={m}
                            onClick={() => setHeatMode(m)}
                            className={`px-3 py-1 text-xs font-medium transition-all cursor-pointer ${
                                heatMode === m ? 'cgm-tab-active' : 'cgm-tab'
                            }`}
                        >
                            {m === 'hr' ? 'HR' : m.charAt(0).toUpperCase() + m.slice(1)}
                        </button>
                    ))}
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                    <OverlayExportWrapper
                        metricName="catapult_heatmap"
                        className="xl:col-span-2"
                        onBeforeCapture={() => setShowOverlay(true)}
                        onAfterCapture={() => setShowOverlay(false)}
                    >
                        <FieldHeatMap points={gpsData} athleteName={athleteName} sessionLabel={sessionLabel} stats={overlayStats} showOverlay={showOverlay} mode={heatMode} onModeChange={setHeatMode} isDark={isDark} />
                    </OverlayExportWrapper>
                    <div className="space-y-3">
                        {actStats && (
                            <div className="space-y-2">
                                {[
                                    { label: 'Distance', value: actStats.total_distance ? (actStats.total_distance >= 1000 ? `${(actStats.total_distance / 1000).toFixed(1)} km` : `${Math.round(actStats.total_distance)} m`) : '—' },
                                    { label: 'Player Load', value: actStats.total_player_load?.toFixed(1) || '—' },
                                    { label: 'Top Speed', value: actStats.max_vel ? `${actStats.max_vel.toFixed(1)} mph` : '—' },
                                    { label: 'Avg HR', value: actStats.mean_heart_rate && actStats.mean_heart_rate > 0 ? `${Math.round(actStats.mean_heart_rate)} bpm` : '—' },
                                    { label: 'Max HR', value: actStats.max_heart_rate && actStats.max_heart_rate > 0 ? `${actStats.max_heart_rate} bpm` : '—' },
                                    { label: 'Max Accel', value: actStats.max_accel && actStats.max_accel > 0 ? `${actStats.max_accel.toFixed(2)} m/s²` : '—' },
                                ].map((s) => (
                                    <div key={s.label} className="flex justify-between items-center py-1.5 border-b">
                                        <span className="text-xs cgm-text-secondary uppercase">{s.label}</span>
                                        <span className="text-sm font-semibold cgm-text-primary">{s.value}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <CatapultAIInsights
                            athleteId={athleteId}
                            sessionStats={actStats}
                            careerStats={null}
                            activityName={selectedSession?.activity_name || 'Unknown session'}
                        />
                    </div>
                </div>
                </>
            )}

            {!gpsLoading && (!gpsData || gpsData.length === 0) && (
                <div className="h-48 flex items-center justify-center text-sm cgm-text-muted">
                    {actStats ? 'No GPS data for this athlete in this activity' : 'Select an activity to view GPS trail'}
                </div>
            )}
        </div>
    );
}


interface CanvasOverlayStats {
    distance: string;
    playerLoad: string;
    topSpeed: string;
    maxHR: string;
}

function drawCanvasOverlayText(ctx: CanvasRenderingContext2D, w: number, athleteName?: string, sessionLabel?: string, stats?: CanvasOverlayStats) {
    if (!athleteName && !stats) return;

    // Top-left: athlete name + session date
    if (athleteName) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        ctx.font = '700 13px Inter, system-ui, sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText(athleteName, 14, 10);

        if (sessionLabel) {
            ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
            ctx.font = '400 8px Inter, system-ui, sans-serif';
            ctx.fillText(sessionLabel, 14, 26);
        }
    }

    // Top-right: stats in a row, right-aligned to match left margin
    if (stats) {
        const items = [
            { label: 'MAX HR', value: stats.maxHR },
            { label: 'TOP SPEED', value: stats.topSpeed },
            { label: 'PLAYER LOAD', value: stats.playerLoad },
            { label: 'DISTANCE', value: stats.distance },
        ];

        ctx.textBaseline = 'top';
        const gap = 72;
        let x = w - 14;

        for (const item of items) {
            ctx.textAlign = 'right';
            ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
            ctx.font = '700 14px Inter, system-ui, sans-serif';
            ctx.fillText(item.value, x, 21);

            ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
            ctx.font = '500 7px Inter, system-ui, sans-serif';
            ctx.fillText(item.label, x, 10);

            x -= gap;
        }
    }
}

function GpsTrailCanvas({
    points,
    hoveredPoint,
    onHover,
    athleteName,
    sessionLabel,
    stats,
}: {
    points: GpsPoint[];
    hoveredPoint: GpsPoint | null;
    onHover: (p: GpsPoint | null) => void;
    athleteName?: string;
    sessionLabel?: string;
    stats?: CanvasOverlayStats;
}) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const timeToColor = (i: number, total: number): string => {
        const t = i / (total - 1 || 1);
        // Cool blue -> warm orange/red
        const r = Math.round(30 + t * 225);
        const g = Math.round(100 + (1 - Math.abs(t - 0.4) * 2.5) * 120);
        const b = Math.round(220 - t * 200);
        return `rgb(${r},${g},${b})`;
    };

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const rect = container.getBoundingClientRect();
        const w = rect.width;
        const h = Math.min(w * 0.75, 500);
        canvas.width = w * 2;
        canvas.height = h * 2;
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;

        const ctx = canvas.getContext('2d')!;
        ctx.scale(2, 2);

        // Background
        ctx.fillStyle = '#91959c';
        ctx.beginPath();
        ctx.roundRect(0, 0, w, h, 8);
        ctx.fill();

        // Field lines
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(w / 2, 0);
        ctx.lineTo(w / 2, h);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(w / 2, h / 2, 40, 0, Math.PI * 2);
        ctx.stroke();

        const pad = 20;
        const drawW = w - pad * 2;
        const drawH = h - pad * 2;

        // Trail
        for (let i = 1; i < points.length; i++) {
            const p0 = points[i - 1];
            const p1 = points[i];
            ctx.beginPath();
            ctx.moveTo(pad + p0.x * drawW, h - pad - p0.y * drawH);
            ctx.lineTo(pad + p1.x * drawW, h - pad - p1.y * drawH);
            ctx.strokeStyle = timeToColor(i, points.length);
            ctx.lineWidth = 1.5;
            ctx.globalAlpha = 0.7;
            ctx.stroke();
        }
        ctx.globalAlpha = 1;

        // Hovered point
        if (hoveredPoint) {
            const hx = pad + hoveredPoint.x * drawW;
            const hy = h - pad - hoveredPoint.y * drawH;
            ctx.beginPath();
            ctx.arc(hx, hy, 6, 0, Math.PI * 2);
            ctx.fillStyle = '#F97316';
            ctx.fill();
            ctx.strokeStyle = '#fff';
            ctx.lineWidth = 2;
            ctx.stroke();
        }

        // Overlay text (name + stats)
        drawCanvasOverlayText(ctx, w, athleteName, sessionLabel, stats);
    }, [points, hoveredPoint, athleteName, sessionLabel, stats]);

    useEffect(() => {
        draw();
        window.addEventListener('resize', draw);
        return () => window.removeEventListener('resize', draw);
    }, [draw]);

    const handleMouseMove = (e: React.MouseEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const mx = (e.clientX - rect.left) / rect.width;
        const my = 1 - (e.clientY - rect.top) / rect.height;

        let closest: GpsPoint | null = null;
        let minDist = Infinity;
        for (const p of points) {
            const dx = p.x - mx;
            const dy = p.y - my;
            const d = dx * dx + dy * dy;
            if (d < minDist) {
                minDist = d;
                closest = p;
            }
        }
        onHover(minDist < 0.002 ? closest : null);
    };

    return (
        <div ref={containerRef}>
            <canvas
                ref={canvasRef}
                className="cursor-crosshair"
                onMouseMove={handleMouseMove}
                onMouseLeave={() => onHover(null)}
            />
        </div>
    );
}


const PLAYER_CHART_OPTIONS = [
    { id: 'player_load', label: 'Player Load', dataKey: 'total_player_load' as keyof AthleteSession, teamAvgKey: 'team_avg_player_load' as keyof AthleteSession, color: '#DC2626' },
    { id: 'distance', label: 'Distance (m)', dataKey: 'total_distance' as keyof AthleteSession, teamAvgKey: 'team_avg_distance' as keyof AthleteSession, color: '#2563EB' },
    { id: 'max_vel', label: 'Max Velocity (mph)', dataKey: 'max_vel' as keyof AthleteSession, teamAvgKey: 'team_avg_max_vel' as keyof AthleteSession, color: '#059669' },
    { id: 'heart_rate', label: 'Heart Rate', dataKey: null, teamAvgKey: null, color: '#D97706' },
    { id: 'hr_zones', label: 'HR Zone Breakdown', dataKey: null, teamAvgKey: null, color: '#EF4444' },
];

function PlayerChartSelector({ sessions, hrMax }: { sessions: AthleteSession[]; hrMax: number | null }) {
    const [selectedChart, setSelectedChart] = useState('player_load');

    return (
        <div className="cgm-card p-4">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold cgm-text-primary">
                    Performance Over Time
                </h3>
                <select
                    value={selectedChart}
                    onChange={(e) => setSelectedChart(e.target.value)}
                    className="cgm-input px-3 py-1.5 text-sm"
                >
                    {PLAYER_CHART_OPTIONS.map((opt) => (
                        <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                </select>
            </div>
            <div className="h-72">
                {selectedChart === 'heart_rate' ? (
                    <HeartRateChartInner sessions={sessions} hrMax={hrMax} />
                ) : selectedChart === 'hr_zones' ? (
                    <HRZoneStackedChartInner sessions={sessions} />
                ) : (() => {
                    const opt = PLAYER_CHART_OPTIONS.find((o) => o.id === selectedChart)!;
                    return <TimeSeriesChartInner
                        title={opt.label}
                        sessions={sessions}
                        dataKey={opt.dataKey!}
                        teamAvgKey={opt.teamAvgKey!}
                        color={opt.color}
                    />;
                })()}
            </div>
        </div>
    );
}

function TimeSeriesChart({
    title,
    sessions,
    dataKey,
    teamAvgKey,
    color,
}: {
    title: string;
    sessions: AthleteSession[];
    dataKey: keyof AthleteSession;
    teamAvgKey?: keyof AthleteSession;
    color: string;
}) {
    const points = sessions
        .filter((s) => (s[dataKey] as number) > 0)
        .map((s) => ({ x: s.date, y: s[dataKey] as number }));

    if (points.length === 0) {
        return (
            <div className="cgm-card p-4">
                <h3 className="text-sm font-semibold cgm-text-primary mb-3">{title}</h3>
                <div className="h-64 flex items-center justify-center text-sm cgm-text-muted">No recent data</div>
            </div>
        );
    }

    const teamAvgPoints = teamAvgKey
        ? sessions
            .filter((s) => (s[teamAvgKey] as number) > 0)
            .map((s) => ({ x: s.date, y: s[teamAvgKey] as number }))
        : [];

    const datasets: any[] = [
        {
            label: title,
            data: points,
            borderColor: color,
            backgroundColor: hexToRgba(color, 0.1),
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            fill: true,
            tension: 0,
        },
    ];

    if (teamAvgPoints.length > 0) {
        datasets.push({
            label: 'Team avg',
            data: teamAvgPoints,
            borderColor: '#9CA3AF',
            borderWidth: 1.5,
            borderDash: [5, 3],
            pointRadius: 0,
            fill: false,
            tension: 0,
        });
    }

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'nearest' as const, intersect: false },
        plugins: {
            legend: { position: 'top' as const, labels: { boxWidth: 12, font: { size: 11 } } },
        },
        scales: {
            x: {
                type: 'time' as const,
                time: { unit: 'day' as const, tooltipFormat: 'MMM d, yyyy' },
                ticks: { maxRotation: 45, font: { size: 10 }, maxTicksLimit: 12 },
            },
            y: {
                beginAtZero: true,
            },
        },
    };

    return (
        <div className="cgm-card p-4">
            <h3 className="text-sm font-semibold cgm-text-primary mb-3">
                {title}
            </h3>
            <div className="h-64">
                <Line data={{ datasets }} options={options} />
            </div>
        </div>
    );
}


function HeartRateChart({
    sessions,
    hrMax,
}: {
    sessions: AthleteSession[];
    hrMax: number | null;
}) {
    const withHR = sessions.filter((s) => s.mean_heart_rate && s.mean_heart_rate > 0);

    if (withHR.length === 0) {
        return (
            <div className="cgm-card p-4">
                <h3 className="text-sm font-semibold cgm-text-primary mb-3">Heart Rate (bpm)</h3>
                <div className="h-64 flex items-center justify-center text-sm cgm-text-muted">No recent heart rate data</div>
            </div>
        );
    }

    const meanPoints = withHR.map((s) => ({ x: s.date, y: s.mean_heart_rate! }));
    const maxPoints = withHR.map((s) => ({ x: s.date, y: s.max_heart_rate || 0 }));

    const datasets: any[] = [
        {
            label: 'Mean HR',
            data: meanPoints,
            borderColor: '#D97706',
            backgroundColor: hexToRgba('#D97706', 0.1),
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            fill: true,
            tension: 0.1,
        },
        {
            label: 'Max HR',
            data: maxPoints,
            borderColor: '#EF4444',
            backgroundColor: hexToRgba('#EF4444', 0.08),
            borderWidth: 1.5,
            pointRadius: 2,
            pointHoverRadius: 4,
            fill: '+1',
            tension: 0.1,
        },
    ];

    if (hrMax && hrMax > 0) {
        const dates = meanPoints.map((p) => p.x);
        datasets.push({
            label: 'HR Max Threshold',
            data: [
                { x: dates[0], y: hrMax },
                { x: dates[dates.length - 1], y: hrMax },
            ],
            borderColor: '#991B1B',
            borderWidth: 1.5,
            borderDash: [8, 4],
            pointRadius: 0,
            fill: false,
        });
    }

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'nearest' as const, intersect: false },
        plugins: {
            legend: { position: 'top' as const, labels: { boxWidth: 12, font: { size: 11 } } },
            tooltip: {
                callbacks: {
                    label: (ctx: any) => `${ctx.dataset.label}: ${Math.round(ctx.parsed.y)} bpm`,
                },
            },
        },
        scales: {
            x: {
                type: 'time' as const,
                time: { unit: 'day' as const, tooltipFormat: 'MMM d, yyyy' },
                ticks: { maxRotation: 45, font: { size: 10 }, maxTicksLimit: 12 },
            },
            y: {
                title: { display: true, text: 'bpm', font: { size: 11 } },
            },
        },
    };

    return (
        <div className="cgm-card p-4">
            <h3 className="text-sm font-semibold cgm-text-primary mb-3">
                Heart Rate (bpm)
            </h3>
            <div className="h-64">
                <Line data={{ datasets }} options={options} />
            </div>
        </div>
    );
}


const HR_ZONE_COLORS = ['#8a8a8a', '#3B82F6', '#22C55E', '#84CC16', '#F97316', '#EF4444', '#DC2626', '#991B1B'];
const HR_ZONE_LABELS = ['Band 1 (Recovery)', 'Band 2 (Easy)', 'Band 3 (Moderate)', 'Band 4 (Tempo)', 'Band 5 (High)', 'Band 6 (Very High)', 'Band 7 (Near Max)', 'Band 8 (Maximal)'];

function HRZoneStackedChart({ sessions }: { sessions: AthleteSession[] }) {
    const withData = sessions.filter(
        (s) => (s.hr_band1_pct || 0) + (s.hr_band2_pct || 0) + (s.hr_band3_pct || 0) + (s.hr_band4_pct || 0) + (s.hr_band5_pct || 0) + (s.hr_band6_pct || 0) + (s.hr_band7_pct || 0) + (s.hr_band8_pct || 0) > 0,
    );

    if (withData.length === 0) {
        return (
            <div className="cgm-card p-4">
                <h3 className="text-sm font-semibold cgm-text-primary mb-3">HR Zone Breakdown</h3>
                <div className="h-64 flex items-center justify-center text-sm cgm-text-muted">No recent heart rate data</div>
            </div>
        );
    }

    const bandKeys: (keyof AthleteSession)[] = ['hr_band1_pct', 'hr_band2_pct', 'hr_band3_pct', 'hr_band4_pct', 'hr_band5_pct', 'hr_band6_pct', 'hr_band7_pct', 'hr_band8_pct'];

    const datasets = bandKeys.map((key, i) => ({
        label: HR_ZONE_LABELS[i],
        data: withData.map((s) => ({ x: s.date, y: (s[key] as number) || 0 })),
        backgroundColor: hexToRgba(HR_ZONE_COLORS[i], 0.6),
        borderColor: HR_ZONE_COLORS[i],
        borderWidth: 1,
        fill: true,
        tension: 0,
        pointRadius: 2,
        pointHoverRadius: 4,
    }));

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'nearest' as const, intersect: false },
        plugins: {
            legend: { position: 'top' as const, labels: { boxWidth: 10, font: { size: 10 } } },
            tooltip: {
                callbacks: {
                    label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%`,
                },
            },
        },
        scales: {
            x: {
                type: 'time' as const,
                time: { unit: 'day' as const, tooltipFormat: 'MMM d, yyyy' },
                ticks: { maxRotation: 45, font: { size: 10 }, maxTicksLimit: 12 },
                stacked: true,
            },
            y: {
                stacked: true,
                title: { display: true, text: '% of session', font: { size: 11 } },
                max: 100,
            },
        },
    };

    return (
        <div className="cgm-card p-4">
            <h3 className="text-sm font-semibold cgm-text-primary mb-3">
                HR Zone Breakdown
            </h3>
            <div className="h-64">
                <Line data={{ datasets }} options={options} />
            </div>
        </div>
    );
}


function TimeSeriesChartInner({
    title, sessions, dataKey, teamAvgKey, color,
}: {
    title: string; sessions: AthleteSession[]; dataKey: keyof AthleteSession;
    teamAvgKey: keyof AthleteSession; color: string;
}) {
    const points = sessions
        .filter((s) => (s[dataKey] as number) > 0)
        .map((s) => ({ x: s.date, y: s[dataKey] as number }));

    if (points.length === 0) {
        return <div className="h-full flex items-center justify-center text-sm cgm-text-muted">No recent data</div>;
    }

    const teamAvgPoints = sessions
        .filter((s) => (s[teamAvgKey] as number) > 0)
        .map((s) => ({ x: s.date, y: s[teamAvgKey] as number }));

    const datasets: any[] = [
        { label: title, data: points, borderColor: color, backgroundColor: hexToRgba(color, 0.1), borderWidth: 2, pointRadius: 3, pointHoverRadius: 5, fill: true, tension: 0 },
    ];
    if (teamAvgPoints.length > 0) {
        datasets.push({ label: 'Team avg', data: teamAvgPoints, borderColor: '#9CA3AF', borderWidth: 1.5, borderDash: [5, 3], pointRadius: 0, fill: false, tension: 0 });
    }

    return (
        <Line data={{ datasets }} options={{
            responsive: true, maintainAspectRatio: false,
            interaction: { mode: 'nearest', intersect: false },
            plugins: { legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 } } } },
            scales: {
                x: { type: 'time', time: { unit: 'day', tooltipFormat: 'MMM d, yyyy' }, ticks: { maxRotation: 45, font: { size: 10 }, maxTicksLimit: 12 } },
                y: { beginAtZero: true },
            },
        }} />
    );
}

function HeartRateChartInner({ sessions, hrMax }: { sessions: AthleteSession[]; hrMax: number | null }) {
    const withHR = sessions.filter((s) => s.mean_heart_rate && s.mean_heart_rate > 0);
    if (withHR.length === 0) {
        return <div className="h-full flex items-center justify-center text-sm cgm-text-muted">No recent heart rate data</div>;
    }

    const datasets: any[] = [
        { label: 'Mean HR', data: withHR.map((s) => ({ x: s.date, y: s.mean_heart_rate! })), borderColor: '#D97706', backgroundColor: hexToRgba('#D97706', 0.1), borderWidth: 2, pointRadius: 3, fill: true, tension: 0 },
        { label: 'Max HR', data: withHR.map((s) => ({ x: s.date, y: s.max_heart_rate || 0 })), borderColor: '#EF4444', backgroundColor: hexToRgba('#EF4444', 0.08), borderWidth: 1.5, pointRadius: 2, fill: '+1', tension: 0 },
    ];
    if (hrMax && hrMax > 0) {
        const dates = withHR.map((s) => s.date);
        datasets.push({ label: 'HR Max Threshold', data: [{ x: dates[0], y: hrMax }, { x: dates[dates.length - 1], y: hrMax }], borderColor: '#991B1B', borderWidth: 1.5, borderDash: [8, 4], pointRadius: 0, fill: false });
    }

    return (
        <Line data={{ datasets }} options={{
            responsive: true, maintainAspectRatio: false,
            interaction: { mode: 'nearest', intersect: false },
            plugins: { legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 } } }, tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${Math.round(ctx.parsed.y)} bpm` } } },
            scales: {
                x: { type: 'time', time: { unit: 'day', tooltipFormat: 'MMM d, yyyy' }, ticks: { maxRotation: 45, font: { size: 10 }, maxTicksLimit: 12 } },
                y: { title: { display: true, text: 'bpm', font: { size: 11 } } },
            },
        }} />
    );
}

function HRZoneStackedChartInner({ sessions }: { sessions: AthleteSession[] }) {
    const withData = sessions.filter(
        (s) => (s.hr_band1_pct || 0) + (s.hr_band2_pct || 0) + (s.hr_band3_pct || 0) + (s.hr_band4_pct || 0) + (s.hr_band5_pct || 0) + (s.hr_band6_pct || 0) + (s.hr_band7_pct || 0) + (s.hr_band8_pct || 0) > 0,
    );
    if (withData.length === 0) {
        return <div className="h-full flex items-center justify-center text-sm cgm-text-muted">No recent heart rate data</div>;
    }

    const bandKeys: (keyof AthleteSession)[] = ['hr_band1_pct', 'hr_band2_pct', 'hr_band3_pct', 'hr_band4_pct', 'hr_band5_pct', 'hr_band6_pct', 'hr_band7_pct', 'hr_band8_pct'];
    const datasets = bandKeys.map((key, i) => ({
        label: HR_ZONE_LABELS[i], data: withData.map((s) => ({ x: s.date, y: (s[key] as number) || 0 })),
        backgroundColor: hexToRgba(HR_ZONE_COLORS[i], 0.6), borderColor: HR_ZONE_COLORS[i], borderWidth: 1, fill: true, tension: 0, pointRadius: 2, pointHoverRadius: 4,
    }));

    return (
        <Line data={{ datasets }} options={{
            responsive: true, maintainAspectRatio: false,
            interaction: { mode: 'nearest', intersect: false },
            plugins: { legend: { position: 'top', labels: { boxWidth: 10, font: { size: 10 } } }, tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%` } } },
            scales: {
                x: { type: 'time', time: { unit: 'day', tooltipFormat: 'MMM d, yyyy' }, ticks: { maxRotation: 45, font: { size: 10 }, maxTicksLimit: 12 }, stacked: true },
                y: { stacked: true, title: { display: true, text: '% of session', font: { size: 11 } }, max: 100 },
            },
        }} />
    );
}
